import type { ReactNode } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  selector?: string;
  staggerDelay?: number;
  threshold?: number;
  rootMargin?: string;
  as?: React.ElementType;
}

export default function ScrollReveal({
  children,
  className = "",
  selector = ".scroll-reveal",
  staggerDelay = 80,
  threshold = 0.12,
  rootMargin = "0px 0px -40px 0px",
  as: Tag = "div",
}: ScrollRevealProps) {
  const ref = useScrollReveal<HTMLDivElement>({
    selector,
    staggerDelay,
    threshold,
    rootMargin,
  });

  const Component = Tag as any;

  return (
    <Component ref={ref} className={className}>
      {children}
    </Component>
  );
}

// ScrollRevealBlock: a single element that animates
interface ScrollRevealBlockProps {
  children: ReactNode;
  className?: string;
  variant?: "fade-up" | "fade-left" | "fade-right" | "scale-in" | "blur-in";
  delay?: number;
  style?: React.CSSProperties;
  as?: React.ElementType;
}

export function ScrollRevealBlock({
  children,
  className = "",
  variant = "fade-up",
  delay = 0,
  style = {},
  as: Tag = "div",
}: ScrollRevealBlockProps) {
  const ref = useScrollReveal<HTMLDivElement>({
    selector: ".scroll-reveal-block",
    staggerDelay: delay,
    threshold: 0.1,
  });

  const variantClasses: Record<string, string> = {
    "fade-up": "scroll-reveal",
    "fade-left": "scroll-reveal-left",
    "fade-right": "scroll-reveal-right",
    "scale-in": "scroll-reveal-scale",
    "blur-in": "scroll-reveal-blur",
  };

  const baseClass = variantClasses[variant] || "scroll-reveal";
  const Component = Tag as any;

  return (
    <div ref={ref} className={className}>
      <Component
        className={`scroll-reveal-block ${baseClass}`}
        style={delay > 0 ? { ...style, transitionDelay: `${delay}ms` } : style}
      >
        {children}
      </Component>
    </div>
  );
}

// ScrollRevealGrid: for grids where each child should animate
interface ScrollRevealGridProps {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
  selector?: string;
}

export function ScrollRevealGrid({
  children,
  className = "",
  staggerDelay = 80,
  selector = ".scroll-reveal",
}: ScrollRevealGridProps) {
  const ref = useScrollReveal<HTMLDivElement>({
    selector,
    staggerDelay,
    threshold: 0.08,
    rootMargin: "0px 0px -30px 0px",
  });

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

// ScrollRevealContainer: wraps a section with header + children
interface ScrollRevealContainerProps {
  children: ReactNode;
  className?: string;
  header?: ReactNode;
  staggerDelay?: number;
}

export function ScrollRevealContainer({
  children,
  className = "",
  header,
  staggerDelay = 80,
}: ScrollRevealContainerProps) {
  const ref = useScrollReveal<HTMLDivElement>({
    selector: ".scroll-reveal",
    staggerDelay,
    threshold: 0.1,
    rootMargin: "0px 0px -40px 0px",
  });

  return (
    <div ref={ref} className={className}>
      {header && <div className="scroll-reveal">{header}</div>}
      {children}
    </div>
  );
}